import { useRef, useState, type ChangeEvent } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";
import { parseCsv, type ParsedCsv } from "../../lib/csv";
import {
  HANEY_TARGETS,
  autoMatchHaney,
  buildHaneyPoints,
  haneyParamsWithData,
} from "../../lib/haney";
import { CsvPointsMapper } from "../../components/CsvPointsMapper";
import { useHaneyPoints, useSaveHaney } from "../../hooks/queries";
import { useUiStore } from "../../stores/uiStore";
import type { DashboardField } from "../../types/contract";

// "Add Haney test" data type: import a Haney H3A CSV → persist the georeferenced points
// **server-side** (BE-light, with a localStorage fallback until that endpoint is live) → each
// parameter is ordinary-kriged across the field and shows as a layer in the map's "Haney test"
// group.
export function HaneyOptions({ field }: { field: DashboardField }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const { data: points = [] } = useHaneyPoints(field.field_id);
  const save = useSaveHaney();
  const withData = haneyParamsWithData(points);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setNote(null);
    try {
      const p = parseCsv(await file.text(), file.name);
      if (!p.rows.length) {
        setNote("That file has no data rows.");
        return;
      }
      setParsed(p);
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Could not read that file.");
    }
  }

  return (
    <div className="flex flex-col gap-2 text-[11px]">
      <div className="text-muted">
        Import a <span className="text-fg">Haney soil-health test</span> CSV
        (H3A panel: WEOC, WEON, inorganic/total P, CO₂-C, …). Points are saved
        to the field, and each parameter is{" "}
        <span className="text-fg">ordinary-kriged</span> across it — the
        surfaces appear in the map’s <span className="text-fg">Haney test</span>{" "}
        group.
      </div>

      {!parsed && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.txt"
            className="hidden"
            onChange={onFile}
            aria-label="Haney CSV file"
          />
          <button
            className="flex items-center gap-1 rounded border border-line px-2 py-1 text-fg hover:bg-hover disabled:opacity-50"
            disabled={save.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {save.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Upload size={12} />
            )}{" "}
            Upload Haney CSV
          </button>
          {points.length > 0 && (
            <span className="text-muted">
              <span className="text-fg">{points.length}</span> point
              {points.length === 1 ? "" : "s"} saved ·{" "}
              <span className="text-fg">{withData.length}</span> parameters
            </span>
          )}
        </div>
      )}

      {parsed && (
        <CsvPointsMapper
          parsed={parsed}
          targets={HANEY_TARGETS}
          autoMatch={autoMatchHaney}
          saveLabel="Import Haney points"
          onCancel={() => setParsed(null)}
          onSaveMapping={(mapping, p) => {
            const { points: pts, skipped } = buildHaneyPoints(p, mapping);
            if (pts.length === 0) {
              setNote(
                "No rows had a valid coordinate — check the Latitude / Longitude columns.",
              );
              return;
            }
            save.mutate({ fieldId: field.field_id, points: pts });
            setParsed(null);
            setNote(
              `Imported ${pts.length} Haney point${pts.length === 1 ? "" : "s"}${skipped ? ` (${skipped} skipped — bad coordinates)` : ""}. Open the map → “Haney test” to view the kriged surfaces.`,
            );
          }}
        />
      )}

      {!parsed && points.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="flex items-center gap-1 text-faint hover:text-red-400"
            onClick={() => save.mutate({ fieldId: field.field_id, points: [] })}
          >
            <Trash2 size={11} /> Clear imported points
          </button>
          <button
            className="text-fg underline hover:no-underline"
            onClick={() => useUiStore.getState().openMap(field.field_id)}
          >
            View on map
          </button>
        </div>
      )}

      {note && <div className="text-muted">{note}</div>}
    </div>
  );
}
